'use client';

import { fetchReviews } from '@/actions';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { SingleReview } from './SingleReview';
import { ChevronBottom } from '@/components/icons';
import { TReview } from '@/types';
import { ButtonSemiTransparent } from '../buttons/ButtonSemiTransparent';

interface IReviews {
  tourId: string;
  totalCount: number;
  children: React.ReactNode;
}

const mockReviews = [
  {
    grade: 4.5,
    description:
      'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
    userName: 'Peter Jackson',
    id: 1,
  },
  {
    grade: 4.5,
    description:
      'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
    userName: 'Peter Jackson',
    id: 2,
  },
  {
    grade: 4.5,
    description:
      'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
    userName: 'Peter Jackson',
    id: 3,
  },
  {
    grade: 4.5,
    description:
      'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
    userName: 'Peter Jackson',
    id: 4,
  },
  {
    grade: 4.5,
    description:
      'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
    userName: 'Peter Jackson',
    id: 5,
  },
  {
    grade: 4.5,
    description:
      'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
    userName: 'Peter Jackson',
    id: 6,
  },
  {
    grade: 4.5,
    description:
      'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
    userName: 'Peter Jackson',
    id: 7,
  },
  {
    grade: 4.5,
    description:
      'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
    userName: 'Peter Jackson',
    id: 8,
  },
  {
    grade: 4.5,
    description:
      'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
    userName: 'Peter Jackson',
    id: 9,
  },
  {
    grade: 4.5,
    description:
      'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
    userName: 'Peter Jackson',
    id: 10,
  },
  {
    grade: 4.5,
    description:
      'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
    userName: 'Peter Jackson',
    id: 11,
  },
  {
    grade: 4.5,
    description:
      'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
    userName: 'Peter Jackson',
    id: 12,
  },
  {
    grade: 4.5,
    description:
      'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
    userName: 'Peter Jackson',
    id: 13,
  },
  {
    grade: 4.5,
    description:
      'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.',
    userName: 'Peter Jackson',
    id: 14,
  },
];

export const Reviews = ({ tourId, totalCount, children }: IReviews) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [reviews, setReviews] = useState<Array<TReview>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [page, setPage] = useState(0);
  const [infiniteScroll, setInfiniteScroll] = useState(false);

  const observerTarget = useRef(null);
  const reviewsRef = useRef<HTMLDivElement | null>(null);
  const [reviewsHeight, setReviewsHeight] = useState<string | 'auto'>('auto');

  const toggleCollapse = () => setIsCollapsed((prevValue) => !prevValue);

  const getReviews = async (tourId: string, page: number, controller: AbortController) => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await fetchReviews(tourId, page, controller);

      setReviews((prevData) => [...prevData, ...data]);
      setPage((prevPage) => prevPage + 1);
      setReviewsHeight('auto');
    } catch (error) {
      if (!controller.signal.aborted) {
        console.error(error);
        if (error instanceof Error) setError(error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    getReviews(tourId, page, controller);

    return () => controller.abort();
  }, []);

  useEffect(() => {
    const currentObserver = observerTarget.current;
    const controller = new AbortController();
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          getReviews(tourId, page, controller);
        }
      },
      { threshold: 1 }
    );

    if (infiniteScroll && currentObserver) {
      observer.observe(currentObserver);
    }

    return () => {
      controller.abort();
      if (currentObserver) {
        observer.unobserve(currentObserver);
      }
    };
  }, [tourId, page, observerTarget, infiniteScroll]);

  useEffect(() => {
    if (reviewsRef.current && isCollapsed && reviews.length > 1) {
      setReviewsHeight(`${reviewsRef.current.clientHeight}px`);
    }
  }, [reviews]);

  return (
    <div className="flex flex-col gap-8">
      <div>
        <div className="flex flex-row justify-between items-center pr-6">
          <h2 className="text-2xl mb-0">Reviews</h2>
          <ChevronBottom
            className={`cursor-pointer duration-200 ${isCollapsed ? 'rotate-0' : '-rotate-180'}`}
            onClick={toggleCollapse}
          />
        </div>
        {children}
      </div>
      <div
        ref={reviewsRef}
        style={{ height: isCollapsed ? `${reviewsHeight}` : '0px' }}
        className="flex flex-col gap-6 overflow-hidden duration-200"
      >
        {reviews.map((review) => (
          <SingleReview key={`${review.userName}${review.grade}`} review={review} />
        ))}
        {isLoading && <h1>Loading...</h1>}
        {!infiniteScroll && reviews.length > 5 && (
          <ButtonSemiTransparent handleClick={() => setInfiniteScroll(true)}>
            See all {totalCount} reviews
          </ButtonSemiTransparent>
        )}
        {infiniteScroll && <div ref={observerTarget}></div>}
      </div>
    </div>
  );
};
